import { KnowledgeKind } from "@growth/schema";

import { useSetup } from "./research";
import { useClaims } from "./workspace";

/** The brand profile parts the ICP builder reads. Voice and channels don't change who buys. */
const PARTS: { kind: KnowledgeKind; why: string }[] = [
  { kind: "PRODUCT", why: "what is being sold and what it costs" },
  { kind: "PERSONAS", why: "who feels the problem" },
  { kind: "COMPETITORS", why: "who buys the alternatives today" },
  { kind: "MARKET", why: "timing and where buyers are" },
];

export type ReadinessPart = {
  key: string;
  label: string;
  why: string;
  state: "confirmed" | "check" | "empty" | "redoing";
};

/**
 * Real data, not sample: has a person confirmed the parts of the brand profile and the claims
 * the ICP is built from? Drafting an ICP waits for this.
 */
export const useBrandReadiness = () => {
  const setup = useSetup();
  const claims = useClaims();
  const pending = setup.isPending || claims.isPending;
  const parts: ReadinessPart[] = PARTS.map(({ kind, why }) => {
    const section = setup.data?.sections.find((item) => item.kind === kind);
    return { key: kind, label: section?.label ?? kind, why, state: section?.state ?? "empty" };
  });
  const approved =
    claims.data?.filter((claim) => claim.kind === "APPROVED" && claim.status === "ACTIVE").length ??
    0;
  const suggested = claims.data?.filter((claim) => claim.status === "SUGGESTED").length ?? 0;
  const claimsPart: ReadinessPart = {
    key: "CLAIMS",
    label: "Claims",
    why: "the only facts outreach may state",
    state: suggested > 0 ? "check" : approved > 0 ? "confirmed" : "empty",
  };
  const all = [...parts, claimsPart];
  return {
    pending,
    error: setup.error ?? claims.error,
    parts: all,
    approvedClaims: approved,
    suggestedClaims: suggested,
    ready: !pending && all.every((part) => part.state === "confirmed"),
    toCheck: all.filter((part) => part.state !== "confirmed").length,
  };
};
